<template>
  <div class="page-stack">
    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Prompts</div>
          <h3 class="section-title">提示词库</h3>
          <p class="section-subtitle">默认先查看模板列表。点击新建或编辑时，再通过模态框维护内容。</p>
        </div>
        <div class="flex flex-wrap gap-3">
          <input v-model.trim="keyword" class="field-input min-w-[220px]" placeholder="搜索模板名称或内容">
          <button class="btn btn-primary" @click="openCreateModal">新建模板</button>
          <button class="btn btn-secondary" @click="loadPrompts">刷新列表</button>
        </div>
      </div>
    </section>

    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Library</div>
          <h3 class="section-title">模板列表</h3>
          <p class="section-subtitle">点击编辑可回填到模态框中修改。</p>
        </div>
        <div class="text-sm text-slate-400">共 {{ pagination.total }} 条</div>
      </div>
      <div class="table-shell mt-6 overflow-auto">
        <table class="data-table">
          <thead>
            <tr>
              <th>名称</th>
              <th>类型</th>
              <th>变量</th>
              <th>内容</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in prompts" :key="item.id">
              <td class="font-semibold text-slate-900">{{ item.name }}</td>
              <td>
                <span class="badge" :class="item.type === 'IMAGE' ? 'badge-warm' : 'badge-success'">{{ item.type }}</span>
              </td>
              <td class="text-sm text-slate-500">
                {{ formatVariables(item.variables) }}
              </td>
              <td class="prompt-content-cell">
                <div
                  class="prompt-content-preview"
                  :title="item.content"
                >
                  {{ item.content }}
                </div>
              </td>
              <td class="text-sm text-slate-500">{{ item.createdAt || '-' }}</td>
              <td>
                <div class="flex flex-wrap gap-2">
                  <button class="btn btn-secondary" @click="openEditModal(item)">编辑</button>
                  <button class="btn btn-secondary" @click="removePrompt(item.id)">删除</button>
                </div>
              </td>
            </tr>
            <tr v-if="!prompts.length">
              <td colspan="6" class="table-empty">当前没有模板记录</td>
            </tr>
          </tbody>
        </table>
      </div>
      <AppPagination
        :page-num="pagination.pageNum"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :total-pages="pagination.totalPages"
        @change-page="handlePageChange"
        @change-size="handlePageSizeChange"
      />
    </section>

    <div v-if="modalVisible" class="modal-backdrop" @click.stop>
      <section class="modal-panel" @click.stop>
        <div class="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <h4 class="text-2xl font-bold text-slate-900">{{ form.id ? '编辑模板' : '新建模板' }}</h4>
            <p class="mt-2 text-sm text-slate-500">维护图片或描述提示词，保存后可在产品页直接选择使用。</p>
          </div>
          <button class="btn btn-secondary" @click="closeModal">关闭</button>
        </div>

        <div class="modal-panel__body mt-6 space-y-4">
          <div>
            <label class="field-label">类型</label>
            <select v-model="form.type" class="field-select">
              <option value="IMAGE">IMAGE</option>
              <option value="DESCRIPTION">DESCRIPTION</option>
            </select>
          </div>
          <div>
            <label class="field-label">模板名称</label>
            <input v-model.trim="form.name" class="field-input" placeholder="例如：毛绒饰品改色">
          </div>
          <div>
            <label class="field-label">模板内容</label>
            <textarea v-model.trim="form.content" class="field-textarea" placeholder="输入提示词内容"></textarea>
            <div class="settings-hint">
              变量语法：<code v-pre>{{变量名}}</code>。例如：
              <code v-pre>Please write for {{species}} in {{color}} tone</code>
            </div>
          </div>
          <div v-if="detectedVariables.length" class="rounded-[1.2rem] border border-slate-200 bg-white/80 p-4">
            <div class="field-label">已检测变量</div>
            <div class="flex flex-wrap gap-2">
              <span v-for="item in detectedVariables" :key="item" class="badge badge-muted">{{ item }}</span>
            </div>
          </div>
        </div>

        <div class="modal-panel__footer">
          <button class="btn btn-primary" @click="submitPrompt">{{ form.id ? '更新模板' : '保存模板' }}</button>
          <button class="btn btn-secondary" @click="resetForm">清空</button>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import AppPagination from '@/components/AppPagination.vue';
import { promptApi } from '@/api';
import { confirmAction, notifyError, notifySuccess } from '@/utils/feedback';

const prompts = ref([]);
const keyword = ref('');
const modalVisible = ref(false);
const pagination = reactive({
  pageNum: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0
});
const form = reactive({
  id: null,
  name: '',
  type: 'IMAGE',
  content: ''
});
const detectedVariables = computed(() => extractVariables(form.content));

function resetForm() {
  form.id = null;
  form.name = '';
  form.type = 'IMAGE';
  form.content = '';
}

function openCreateModal() {
  resetForm();
  modalVisible.value = true;
}

function openEditModal(item) {
  form.id = item.id;
  form.name = item.name;
  form.type = item.type;
  form.content = item.content;
  modalVisible.value = true;
}

function closeModal() {
  modalVisible.value = false;
}

function extractVariables(content) {
  const matches = String(content || '').match(/\{\{\s*([\w\u4e00-\u9fa5-]+)\s*\}\}/g) || [];
  return [...new Set(matches.map((item) => item.replace(/\{\{|\}\}/g, '').trim()))];
}

function formatVariables(variables = []) {
  const names = (variables || []).map((item) => item.name).filter(Boolean);
  return names.length ? names.join(', ') : '-';
}

async function loadPrompts() {
  try {
    const res = await promptApi.list({
      keyword: keyword.value || undefined,
      pageNum: pagination.pageNum,
      pageSize: pagination.pageSize
    });
    prompts.value = res.data.records || [];
    pagination.total = res.data.total || 0;
    pagination.totalPages = res.data.totalPages || 0;
    if (!prompts.value.length && pagination.pageNum > 1 && pagination.total > 0) {
      pagination.pageNum -= 1;
      await loadPrompts();
    }
  } catch (error) {
    notifyError(error.message || '加载提示词失败');
  }
}

async function submitPrompt() {
  if (!form.name || !form.content) {
    notifyError('请填写完整信息');
    return;
  }
  try {
    const isEdit = Boolean(form.id);
    const payload = {
      name: form.name,
      type: form.type,
      content: form.content
    };
    if (form.id) {
      await promptApi.update(form.id, payload);
    } else {
      await promptApi.create(payload);
    }
    closeModal();
    resetForm();
    await loadPrompts();
    notifySuccess(isEdit ? '模板已更新' : '模板已保存');
  } catch (error) {
    notifyError(error.message || '保存失败');
  }
}

async function removePrompt(id) {
  if (!(await confirmAction({
    title: '删除提示词模板',
    message: '确认删除这条模板吗？删除后将不能在产品页继续复用。',
    confirmText: '确认删除',
    tone: 'danger'
  }))) {
    return;
  }
  try {
    await promptApi.remove(id);
    if (prompts.value.length === 1 && pagination.pageNum > 1) {
      pagination.pageNum -= 1;
    }
    await loadPrompts();
    notifySuccess('模板已删除');
  } catch (error) {
    notifyError(error.message || '删除失败');
  }
}

function handlePageChange(pageNum) {
  pagination.pageNum = pageNum;
  loadPrompts();
}

function handlePageSizeChange(pageSize) {
  pagination.pageSize = pageSize;
  pagination.pageNum = 1;
  loadPrompts();
}

watch(keyword, () => {
  pagination.pageNum = 1;
  loadPrompts();
});

onMounted(loadPrompts);
</script>
